import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Play, Pause, Music2 } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { AUDIO_URL } from "@/constants/assets";

export function AudioPlayer() {
  const { t } = useLanguage();
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [volume, setVolume] = useState(0.5);

  const fadeTo = (target, then) => {
    const el = audioRef.current;
    if (!el) return;
    const step = () => {
      const diff = target - el.volume;
      if (Math.abs(diff) < 0.03) { el.volume = target; then && then(); return; }
      el.volume = el.volume + diff * 0.12;
      requestAnimationFrame(step);
    };
    step();
  };

  const toggle = () => {
    const el = audioRef.current;
    if (!el) return;
    if (playing) {
      fadeTo(0, () => { el.pause(); setPlaying(false); });
    } else {
      el.volume = 0;
      el.play().then(() => { setPlaying(true); fadeTo(volume); }).catch(() => setPlaying(false));
    }
  };

  const onVolume = (v) => {
    setVolume(v);
    if (audioRef.current) audioRef.current.volume = v;
  };

  return (
    <motion.div
      data-testid="audio-player"
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 1.6, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className="fixed bottom-6 right-6 z-50 flex items-center gap-3 pl-4 pr-3 py-3 bg-[#14120F]/90 backdrop-blur-xl border hairline shadow-2xl shadow-black/60"
    >
      <audio ref={audioRef} src={AUDIO_URL} loop preload="none" />
      <div className={`flex items-end gap-[3px] h-5 ${playing ? "" : "eq-paused"}`} aria-hidden>
        {[0.9, 0.6, 1.1, 0.75].map((d, i) => (
          <span key={i} className="eq-bar w-[3px] h-full bg-[#D4AF37]" style={{ animationDuration: `${d}s` }} />
        ))}
      </div>
      <div className="hidden sm:block leading-tight">
        <div className="text-[9px] font-mono-d tracking-[0.3em] uppercase text-[#7A7367]">{t("audio_label")}</div>
        <div className="text-xs text-[#F7F4EE] font-serif-d italic">{t("audio_track")}</div>
      </div>
      <input
        data-testid="audio-volume-slider"
        type="range" min="0" max="1" step="0.01" value={volume}
        onChange={(e) => onVolume(parseFloat(e.target.value))}
        className="hidden md:block w-16 h-[3px] accent-[#D4AF37] cursor-pointer"
        aria-label="Volume"
      />
      <button
        data-testid="audio-player-toggle-button"
        onClick={toggle}
        className="w-10 h-10 rounded-full border border-[#D4AF37] text-[#D4AF37] flex items-center justify-center hover:bg-[#D4AF37] hover:text-[#0B0A08] transition-colors duration-300"
        aria-label={playing ? "Pause" : "Play"}
      >
        {playing ? <Pause size={16} /> : <Play size={16} className="ml-0.5" />}
      </button>
    </motion.div>
  );
}

export function AudioHint() {
  return <Music2 size={14} className="text-[#C5A059]" />;
}
